import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIConfigurationAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [actionStatus, setActionStatus] = useState({ type: '', message: '' });
  const [appliedSuggestions, setAppliedSuggestions] = useState(new Set());
  const [businessProfile, setBusinessProfile] = useState({
    industry: '',
    size: '',
    region: '',
    primaryGoals: []
  });

  const configurationSteps = [
    {
      title: 'Business Profile',
      description: 'Help us understand your business to provide optimal settings',
      questions: [
        {
          id: 'industry',
          label: 'What industry are you in?',
          type: 'select',
          options: [
            'Technology',
            'Healthcare',
            'Finance',
            'Retail',
            'Manufacturing',
            'Professional Services',
            'Other'
          ]
        },
        {
          id: 'size',
          label: 'What is your company size?',
          type: 'select',
          options: [
            '1-10 employees',
            '11-50 employees',
            '51-200 employees',
            '201-1000 employees',
            '1000+ employees'
          ]
        }
      ]
    },
    {
      title: 'Configuration Preferences',
      description: 'AI will configure optimal settings based on your preferences',
      recommendations: [
        'Automated VAT calculation for your region',
        'Industry-specific chart of accounts',
        'Compliance rules for your business type',
        'Workflow automation for common processes'
      ]
    }
  ];

  const suggestions = [
    {
      id: 'tax_config',
      category: 'Tax Configuration',
      title: 'Setup automated VAT calculation',
      description: 'Based on your location, we recommend enabling automated VAT calculation with current rates.',
      impact: 'High',
      effort: 'Low',
      estimatedTime: '2 minutes'
    },
    {
      id: 'workflow_automation',
      category: 'Workflow Automation',
      title: 'Enable approval workflows',
      description: 'Set up approval workflows for expenses over $500 to improve financial controls.',
      impact: 'Medium',
      effort: 'Medium',
      estimatedTime: '5 minutes'
    },
    {
      id: 'user_management',
      category: 'User Management',
      title: 'Configure role-based access',
      description: 'Setup role-based permissions to ensure data security and proper access controls.',
      impact: 'High',
      effort: 'Medium',
      estimatedTime: '3 minutes'
    },
    {
      id: 'reporting',
      category: 'Reporting',
      title: 'Enable automated reports',
      description: 'Schedule monthly financial reports to be automatically generated and emailed.',
      impact: 'Medium',
      effort: 'Low',
      estimatedTime: '1 minute'
    }
  ];

  const quickActions = [
    {
      id: 'auto_configure',
      icon: 'Zap',
      title: 'Auto-configure',
      description: 'Apply all recommended settings',
      action: 'applyAllSuggestions'
    },
    {
      id: 'setup_guide',
      icon: 'FileText',
      title: 'Setup Guide',
      description: 'Follow step-by-step guide',
      action: 'openSetupGuide'
    },
    {
      id: 'team_setup',
      icon: 'Users',
      title: 'Team Setup',
      description: 'Configure user roles',
      action: 'openTeamSetup'
    },
    {
      id: 'security_setup',
      icon: 'Shield',
      title: 'Security Setup',
      description: 'Configure security settings',
      action: 'openSecuritySetup'
    }
  ];

  const handleClose = () => {
    setIsOpen(false);
    setCurrentStep(0);
    setActionStatus({ type: '', message: '' });
  };

  const handleApplySuggestion = async (suggestion) => {
    if (appliedSuggestions?.has(suggestion?.id)) return;

    setIsLoading(true);
    setActionStatus({ type: '', message: '' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Applying suggestion:', suggestion);
      
      setAppliedSuggestions(prev => new Set([...prev, suggestion.id]));
      
      setActionStatus({ 
        type: 'success', 
        message: `${suggestion?.title} applied successfully!` 
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: `Failed to apply ${suggestion?.title}. Please try again.` 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyAllSuggestions = async () => {
    const unappliedSuggestions = suggestions?.filter(s => !appliedSuggestions?.has(s?.id));
    
    if (unappliedSuggestions?.length === 0) {
      setActionStatus({ 
        type: 'info', 
        message: 'All suggestions have already been applied!' 
      });
      return;
    }

    const confirmed = window.confirm(
      `This will apply ${unappliedSuggestions?.length} configuration suggestions. Continue?`
    );
    if (!confirmed) return;

    setIsLoading(true);
    setActionStatus({ type: '', message: '' });

    try {
      // Simulate batch API call
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      console.log('Applying all suggestions:', unappliedSuggestions);
      
      // Mark all as applied
      setAppliedSuggestions(prev => {
        const newSet = new Set(prev);
        unappliedSuggestions?.forEach(s => newSet?.add(s?.id));
        return newSet;
      });
      
      setActionStatus({ 
        type: 'success', 
        message: `Successfully applied ${unappliedSuggestions?.length} configuration suggestions!` 
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 5000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to apply some suggestions. Please try individual suggestions.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = async (action) => {
    setIsLoading(true);
    setActionStatus({ type: '', message: '' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      switch (action) {
        case 'applyAllSuggestions':
          await handleApplyAllSuggestions();
          return;
        case 'openSetupGuide':
          setActionStatus({ 
            type: 'success', 
            message: 'Setup guide opened successfully!' 
          });
          break;
        case 'openTeamSetup':
          setActionStatus({ 
            type: 'success', 
            message: 'Team setup wizard launched!' 
          });
          break;
        case 'openSecuritySetup':
          setActionStatus({ 
            type: 'success', 
            message: 'Security configuration opened!' 
          });
          break;
        default:
          setActionStatus({ 
            type: 'success', 
            message: 'Action completed successfully!' 
          });
      }

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to complete action. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonText = (suggestion) => {
    if (appliedSuggestions?.has(suggestion?.id)) {
      return 'Applied';
    }
    return 'Apply';
  };

  const getButtonIcon = (suggestion) => {
    if (appliedSuggestions?.has(suggestion?.id)) {
      return 'Check';
    }
    return 'Plus';
  };

  const appliedCount = appliedSuggestions?.size;
  const totalSuggestions = suggestions?.length;
  const progressPercentage = (appliedCount / totalSuggestions) * 100;

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setIsOpen(true)}
        iconName="Sparkles"
      >
        AI Assistant
      </Button>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Sparkles" size={16} color="white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">AI Configuration Assistant</h3>
                  <p className="text-sm text-muted-foreground">Get personalized configuration recommendations</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleClose}
                disabled={isLoading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            {/* Progress Bar */}
            {appliedCount > 0 && (
              <div className="px-6 py-3 bg-muted/30 border-b border-border">
                <div className="flex items-center justify-between text-sm text-foreground mb-2">
                  <span>Configuration Progress</span>
                  <span>{appliedCount}/{totalSuggestions} completed</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Status Messages */}
            {actionStatus?.message && (
              <div className={`mx-6 mt-4 p-3 rounded-lg border ${
                actionStatus?.type === 'success' ?'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
                  : actionStatus?.type === 'info' ?'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400' :'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
              }`}>
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={
                      actionStatus?.type === 'success' ? 'CheckCircle' : 
                      actionStatus?.type === 'info' ? 'Info' : 'AlertCircle'
                    } 
                    size={16} 
                  />
                  <span className="text-sm">{actionStatus?.message}</span>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Smart Suggestions */}
              <div className="mb-8">
                <h4 className="text-md font-medium text-foreground mb-4">Smart Configuration Suggestions</h4>
                <div className="space-y-4">
                  {suggestions?.map((suggestion) => (
                    <div key={suggestion?.id} className={`border rounded-lg p-4 transition-all ${
                      appliedSuggestions?.has(suggestion?.id) 
                        ? 'border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/10' :'border-border hover:border-primary/50'
                    }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xs font-medium px-2 py-1 bg-primary/10 text-primary rounded-full">
                              {suggestion?.category}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              suggestion?.impact === 'High' ?'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' 
                                : suggestion?.impact === 'Medium'?'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400' :'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                            }`}>
                              {suggestion?.impact} Impact
                            </span>
                            {appliedSuggestions?.has(suggestion?.id) && (
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full dark:bg-green-900/20 dark:text-green-400">
                                Applied
                              </span>
                            )}
                          </div>
                          <h5 className="font-medium text-foreground mb-1">{suggestion?.title}</h5>
                          <p className="text-sm text-muted-foreground">{suggestion?.description}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleApplySuggestion(suggestion)}
                          iconName={getButtonIcon(suggestion)}
                          disabled={appliedSuggestions?.has(suggestion?.id) || isLoading}
                          className={appliedSuggestions?.has(suggestion?.id) ? 'opacity-50' : ''}
                        >
                          {getButtonText(suggestion)}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <Icon name="Clock" size={12} className="mr-1" />
                            <span>Effort: {suggestion?.effort}</span>
                          </div>
                          <div className="flex items-center">
                            <Icon name="Timer" size={12} className="mr-1" />
                            <span>{suggestion?.estimatedTime}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <h4 className="text-md font-medium text-foreground mb-4">Quick Setup Actions</h4>
                <div className="grid grid-cols-2 gap-4">
                  {quickActions?.map((action) => (
                    <Button 
                      key={action?.id}
                      variant="outline" 
                      className="justify-start h-auto p-4"
                      onClick={() => handleQuickAction(action?.action)}
                      disabled={isLoading}
                    >
                      <div className="text-left">
                        <div className="flex items-center space-x-2 mb-1">
                          <Icon name={action?.icon} size={16} />
                          <span className="font-medium">{action?.title}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {action?.description}
                        </p>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Info" size={14} />
                <span>Recommendations are based on industry best practices</span>
              </div>
              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  {appliedCount > 0 ? 'Close' : 'Maybe Later'}
                </Button>
                <Button 
                  iconName="Sparkles"
                  onClick={handleApplyAllSuggestions}
                  disabled={isLoading || appliedCount === totalSuggestions}
                  className={isLoading ? 'opacity-50' : ''}
                >
                  {isLoading ? (
                    <>
                      <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                      Applying...
                    </>
                  ) : (
                    appliedCount === totalSuggestions ? 'All Applied' : 'Apply All Suggestions'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIConfigurationAssistant;